// Compliance checklist extracted from a compliance request's sample document.
//
// The backend proxies the uploaded sample to Gemini and hands the model's JSON
// back verbatim; we show it for review and then persist it on the compliance
// request as `checklistExtraction`, which the backend stores opaquely (no
// server-side schema validation).
//
// The fields are snake_case on purpose: this is the model's own JSON, shaped by
// LLMExtractionSchemas.complianceChecklistExtraction, and it is persisted and
// read back exactly as received. Do not camelCase it on the way through.

// Values the model returns for `document_type`, mapped to what we show.
export const CHECKLIST_DOCUMENT_TYPE_DISPLAY_NAMES = {
	COI: 'Certificate of Insurance',
	W9: 'W-9',
	BUSINESS_LICENSE: 'Business License',
	TRADE_LICENSE: 'Trade License',
	OTHER: 'Other document',
};

// How a single requirement is verified against a supplier's document.
export const CHECKLIST_CHECK_TYPES = {
	PRESENT: 'PRESENT',
	ABSENT: 'ABSENT',
	EQUALS: 'EQUALS',
	CONTAINS: 'CONTAINS',
	ONE_OF: 'ONE_OF',
	MIN_AMOUNT: 'MIN_AMOUNT',
	MAX_AMOUNT: 'MAX_AMOUNT',
	CHECKBOX_CHECKED: 'CHECKBOX_CHECKED',
	CHECKBOX_UNCHECKED: 'CHECKBOX_UNCHECKED',
	DATE_PRESENT: 'DATE_PRESENT',
	DATE_IN_EFFECT: 'DATE_IN_EFFECT',
	DATE_NOT_EXPIRED: 'DATE_NOT_EXPIRED',
	SIGNATURE_PRESENT: 'SIGNATURE_PRESENT',
};

// How strictly text is compared (EQUALS / CONTAINS / ONE_OF only).
export const CHECKLIST_MATCH_MODE_DISPLAY_NAMES = {
	EXACT: 'exact match',
	NORMALIZED: 'allowing formatting differences',
	SEMANTIC: 'allowing equivalent wording',
};

export const CHECKLIST_ITEM_SEVERITIES = {
	REQUIRED: 'REQUIRED',
	RECOMMENDED: 'RECOMMENDED',
};

export const CHECKLIST_CONFIDENCE_LEVELS = {
	HIGH: 'HIGH',
	MEDIUM: 'MEDIUM',
	LOW: 'LOW',
};

export const CHECKLIST_CONFIDENCE_DISPLAY_NAMES = {
	[CHECKLIST_CONFIDENCE_LEVELS.HIGH]: 'High confidence',
	[CHECKLIST_CONFIDENCE_LEVELS.MEDIUM]: 'Medium confidence',
	[CHECKLIST_CONFIDENCE_LEVELS.LOW]: 'Low confidence',
};

export interface ChecklistExtractionItem {
	// Stable identifier, unique within the extraction.
	key: string;
	// 1-based, top-to-bottom / left-to-right as the requirement appears on the form.
	display_order: number;
	// Section of the source document, e.g. "General Liability". Drives the grouping
	// in the review screen.
	section: string;
	// Ties related items together, e.g. every additional insured named in the same
	// free-text block. Null when the item stands alone.
	group?: string;
	label: string;
	// Where on the form the requirement lives, e.g.
	// "Coverages > General Liability > Each Occurrence".
	field_path: string;
	check_type: string;
	// Exactly one of expected_text / expected_number / expected_options is
	// populated, per the check type; the others come back null / empty.
	expected_text?: string;
	expected_number?: number;
	expected_options: string[];
	match_mode?: string;
	severity: string;
	confidence: string;
	// What in the sample indicated this requirement — a quoted annotation or the
	// filled-in value and its position. Shown to the buyer as the audit trail.
	evidence: string;
	rationale: string;
}

// A blank or ambiguous field the model could not turn into a requirement on its
// own, surfaced to the buyer as a question.
export interface ChecklistExtractionUnresolved {
	field_path: string;
	observation: string;
	question_for_customer: string;
}

export interface ChecklistExtraction {
	document_type: string;
	// e.g. "ACORD 25 (2010/05)". Null when the sample is not a standard form.
	form_identifier?: string;
	// How the requirements were indicated in the sample (annotations, filled-in
	// values, placeholder text, ...).
	source_notes?: string;
	items: ChecklistExtractionItem[];
	unresolved: ChecklistExtractionUnresolved[];
}

// ---------------------------------------------------------------------------
// Evaluation — a supplier's document checked against the checklist above.
//
// Same rule as the extraction: this is the model's own JSON, returned verbatim
// by POST /compliance/request_instances/:id/document/extract_data and persisted
// verbatim on the instance as `checklistEvaluation`. snake_case throughout; do
// not camelCase it on the way through.
// ---------------------------------------------------------------------------

// Per-item verdict. NEEDS_HUMAN_REVIEW means the model would not guess — the
// value was illegible, two places on the document disagreed, or a semantic match
// was plausible but not clearly equivalent.
export const CHECKLIST_RESULT_STATUSES = {
	SATISFIED: 'SATISFIED',
	NOT_SATISFIED: 'NOT_SATISFIED',
	NEEDS_HUMAN_REVIEW: 'NEEDS_HUMAN_REVIEW',
};

// Something wrong with the upload as a whole, independent of any single item.
export const CHECKLIST_DOCUMENT_ISSUES = {
	NONE: 'NONE',
	WRONG_DOCUMENT_TYPE: 'WRONG_DOCUMENT_TYPE',
	UNREADABLE: 'UNREADABLE',
	INCOMPLETE: 'INCOMPLETE',
	MULTIPLE_CERTIFICATES: 'MULTIPLE_CERTIFICATES',
};

// Supplier-facing copy per document issue. The model's own
// `document_issue_detail` is shown underneath as the specifics.
export const CHECKLIST_DOCUMENT_ISSUE_SUPPLIER_MESSAGES = {
	[CHECKLIST_DOCUMENT_ISSUES.WRONG_DOCUMENT_TYPE]:
		'This does not look like the document your client asked for, so we could not check it.',
	[CHECKLIST_DOCUMENT_ISSUES.UNREADABLE]:
		'We could not read this file clearly enough to check it. A clearer scan or the original PDF from your broker usually fixes this.',
	[CHECKLIST_DOCUMENT_ISSUES.INCOMPLETE]:
		'This document refers to pages or endorsements that were not included, so some requirements could not be checked.',
	[CHECKLIST_DOCUMENT_ISSUES.MULTIPLE_CERTIFICATES]:
		'This file contains more than one certificate, so it is unclear which one applies to this request.',
};

// Overall verdict for an evaluation. NOT returned by the model — the backend
// stores the response verbatim and leaves this to us, so it is always derived
// from the results plus the checklist items' severities (see
// deriveOverallEvaluationStatus in ComplianceEvaluationUtils).
export const EVALUATION_OVERALL_STATUSES = {
	COMPLIANT: 'COMPLIANT',
	NON_COMPLIANT: 'NON_COMPLIANT',
	NEEDS_HUMAN_REVIEW: 'NEEDS_HUMAN_REVIEW',
};

export interface ChecklistEvaluationResult {
	// Joins back to the checklist item's `key`. Section, label and severity live
	// on the item, not here — the result carries only what was read from the
	// document.
	item_key: string;
	status: string;
	// Verbatim from the document: dates as written, names as written.
	found_text?: string;
	// Parsed dollar amount, for MIN_AMOUNT / MAX_AMOUNT.
	found_number?: number;
	// Where on the document it was found, e.g.
	// "p1, General Liability row, Each Occurrence".
	location: string;
	// Confidence in the FOUND value, not in the verdict.
	confidence: string;
	// Internal-facing explanation of the verdict.
	reason: string;
	// Supplier/broker-facing failure line; empty string when SATISFIED.
	supplier_message: string;
}

export interface ChecklistEvaluation {
	document_type_detected: string;
	document_issue: string;
	document_issue_detail?: string;
	// 2-4 plain sentences a supplier or their broker can act on.
	summary_for_supplier: string;
	// One per checklist item, in checklist order. Empty only when document_issue
	// is WRONG_DOCUMENT_TYPE or UNREADABLE.
	results: ChecklistEvaluationResult[];
	// Reviewer-relevant notes no checklist item covers, e.g. a policy expiring
	// within the warning window.
	unmapped_observations: string[];
}
